import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Sparkles, Wand2, ScanEye, FileSearch, Calculator, LayoutGrid,
  ArrowRight, Clock, Loader2,
} from "lucide-react";

const API = `${API_BASE}/api`;

// Static tile config — the 5 rep-facing tools that now live under Prospect IQ.
// Every field is self-explanatory; adding a new tool is one entry here plus
// the matching route in App.js.
const TOOLS = [
  {
    key: "proposals",
    title: "Prospect IQ Proposal",
    blurb: "Answer 6 questions, get a branded proposal deck with pricing that stitches the right slides.",
    icon: Sparkles,
    color: "from-indigo-600 to-purple-600",
    href: "/prospect-proposals",
    testid: "hub-proposals",
  },
  {
    key: "competitor",
    title: "Competitor Reports",
    blurb: "Claude researches 3 competitors live — reviews, ads, positioning, SEO — and hands back a branded deck.",
    icon: ScanEye,
    color: "from-sky-600 to-cyan-600",
    href: "/prospect-iq/competitor-reports",
    testid: "hub-competitor",
    badge: "NEW",
  },
  {
    key: "mockup",
    title: "Homepage Mockup",
    blurb: "On the fly, show a prospect what a modernized homepage could look like. Ready in 60-90s.",
    icon: Wand2,
    color: "from-fuchsia-600 to-pink-600",
    href: "/mockups",
    testid: "hub-mockup",
  },
  {
    key: "audit",
    title: "Proposals & Audits",
    blurb: "Track every proposal & AEO/SEO audit the team has generated. Filter, re-open, or re-send.",
    icon: FileSearch,
    color: "from-emerald-600 to-teal-600",
    href: "/proposals",
    testid: "hub-audit",
  },
  {
    key: "roi",
    title: "ROI Estimator",
    blurb: "Live 'missed opportunity' estimate for a prospect based on missed calls, close rate & LTV.",
    icon: Calculator,
    color: "from-amber-600 to-orange-600",
    href: "/resources/roi-estimator",
    testid: "hub-roi",
  },
  {
    key: "slides",
    title: "Slide Library",
    blurb: "Browse every master-deck slide, pick the ones you want, and download them as one custom PPTX.",
    icon: LayoutGrid,
    color: "from-slate-700 to-slate-900",
    href: "/resources/slide-library",
    testid: "hub-slides",
  },
];

export default function ProspectIqHubPage() {
  const { user } = useAuth();
  const [recent, setRecent] = useState({ competitor: [], proposals: [], mockups: [] });
  const [loading, setLoading] = useState(true);

  // Pull the three "generated artefact" lists in parallel — failures on any
  // one shouldn't blank out the whole hub (e.g. reps sometimes lack a tenant
  // yet have access to the tools). Swallow individual errors.
  useEffect(() => {
    (async () => {
      try {
        const [cr, pr, mk] = await Promise.allSettled([
          axios.get(`${API}/competitor-reports`),
          axios.get(`${API}/prospect-proposals`),
          axios.get(`${API}/mockups`),
        ]);
        setRecent({
          competitor: cr.status === "fulfilled" ? (cr.value.data || []).slice(0, 3) : [],
          proposals:  pr.status === "fulfilled" ? (pr.value.data || []).slice(0, 3) : [],
          mockups:    mk.status === "fulfilled" ? (mk.value.data || []).slice(0, 3) : [],
        });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-8" data-testid="prospect-iq-hub">
      <div>
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-br from-indigo-600 to-purple-600 text-white p-3 rounded-xl shadow">
            <Sparkles className="h-6 w-6" />
          </div>
          <div>
            <h1 className="font-outfit text-3xl font-bold tracking-tight">Prospect IQ</h1>
            <p className="text-muted-foreground text-sm mt-0.5">
              Every prospect-facing tool in one place — proposals, competitor research, mockups, audits.
            </p>
          </div>
        </div>
      </div>

      {/* Tool tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {TOOLS.map((t) => {
          const Icon = t.icon;
          return (
            <Link
              key={t.key}
              to={t.href}
              className="group border rounded-2xl bg-white p-5 shadow-sm hover:shadow-md hover:border-indigo-300 transition relative"
              data-testid={t.testid}
            >
              <div className={`bg-gradient-to-br ${t.color} text-white w-11 h-11 rounded-xl flex items-center justify-center shadow-sm`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="mt-3 flex items-center gap-2">
                <h3 className="font-semibold">{t.title}</h3>
                {t.badge && <Badge className="bg-indigo-600 hover:bg-indigo-600 text-white text-[10px]">{t.badge}</Badge>}
              </div>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{t.blurb}</p>
              <div className="mt-3 text-xs font-medium text-indigo-600 flex items-center opacity-0 group-hover:opacity-100 transition">
                Open <ArrowRight className="h-3 w-3 ml-1" />
              </div>
            </Link>
          );
        })}
      </div>

      {/* Recent activity — 3 stacked columns */}
      <div>
        <h2 className="font-outfit text-lg font-bold flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          Recent activity
        </h2>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
            <RecentColumn
              title="Competitor Reports"
              icon={ScanEye}
              items={recent.competitor.map((r) => ({
                id: r.id, title: r.business_name, sub: r.status,
                href: `/prospect-iq/competitor-reports/${r.id}`,
              }))}
              emptyCta={{ href: "/prospect-iq/competitor-reports/new", label: "Start a report" }}
            />
            <RecentColumn
              title="Proposals"
              icon={Sparkles}
              items={recent.proposals.map((r) => ({
                id: r.id, title: r.business_name || "Untitled", sub: r.status || "",
                href: `/prospect-proposals?id=${r.id}`,
              }))}
              emptyCta={{ href: "/prospect-proposals", label: "Build a proposal" }}
            />
            <RecentColumn
              title="Mockups"
              icon={Wand2}
              items={recent.mockups.map((r) => ({
                id: r.id, title: r.business_name || "Untitled", sub: r.status || "",
                href: `/mockups?id=${r.id}`,
              }))}
              emptyCta={{ href: "/mockups", label: "Draft a mockup" }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function RecentColumn({ title, icon: Icon, items, emptyCta }) {
  return (
    <Card className="border">
      <CardContent className="pt-4">
        <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-wide text-muted-foreground font-semibold">
          <Icon className="h-3.5 w-3.5" /> {title}
        </div>
        {items.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-xs text-muted-foreground mb-2">Nothing yet</p>
            <Link to={emptyCta.href} className="text-xs font-medium text-indigo-600 hover:underline">
              {emptyCta.label} →
            </Link>
          </div>
        ) : (
          <ul className="space-y-2">
            {items.map((it) => (
              <li key={it.id}>
                <Link to={it.href} className="block rounded-lg border px-3 py-2 hover:bg-zinc-50 transition">
                  <div className="text-sm font-medium truncate">{it.title}</div>
                  {it.sub && <div className="text-[11px] text-muted-foreground">{it.sub}</div>}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
